import axios from "axios";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { intlFormatDistance } from "date-fns";
import { Markdown } from "@tecnobit-srl/content-types-markdown/lazy";
import { Button } from "@tecnobit-srl/content-types-button/lazy";
import { DatePicker } from "@tecnobit-srl/content-types-form-date";
import { FontAwesome } from "@tecnobit-srl/ui-font-awesome";
import { DataTable, createDataTableColumnHelper, useDataTable } from "@tecnobit-srl/ui-data-table";
import { useTranslation } from "react-i18next";
import { usePage, useRouteContextHeaders } from "@tecnobit-srl/router";
import { useShowToast } from "@tecnobit-srl/toasts";
import { getVisitorTimeZone, localDateTimeToUtcIso, utcIsoToLocalDateTimeInput } from "@tecnobit-srl/consultancies-types";
import { usePagination } from "@tecnobit-srl/hook-use-pagination";
import { jsx, jsxs } from "react/jsx-runtime";
//#region \0rolldown/runtime.js
var __defProp = Object.defineProperty;
var __exportAll = (all, no_symbols) => {
	let target = {};
	for (var name in all) __defProp(target, name, {
		get: all[name],
		enumerable: true
	});
	if (!no_symbols) __defProp(target, Symbol.toStringTag, { value: "Module" });
	return target;
};
//#endregion
//#region ../../../core/styled-system/styled-system/helpers.js
function isObject(v) {
	return typeof v === "object" && v != null && !Array.isArray(v);
}
var HAS_OWN = Object.prototype.hasOwnProperty;
function isBaseCondition(v) {
	return v === "base";
}
function filterBaseConditions(c) {
	const out = [];
	for (let i = 0; i < c.length; i++) if (!isBaseCondition(c[i])) out.push(c[i]);
	return out;
}
function toHash(v) {
	let h = 5381;
	for (let i = v.length; i;) h = h * 33 ^ v.charCodeAt(--i);
	let x = h >>> 0, out = "";
	for (; x > 52; x = x / 52 | 0) {
		const c = x % 52;
		out = String.fromCharCode(c + (c > 25 ? 39 : 97)) + out;
	}
	const c = x % 52;
	return String.fromCharCode(c + (c > 25 ? 39 : 97)) + out;
}
function toResponsiveObject(values, breakpoints) {
	const out = {};
	for (let i = 0; i < values.length; i++) if (values[i] != null) out[breakpoints[i]] = values[i];
	return out;
}
function walkObject(target, fn, options) {
	options ||= {};
	const { stop, getKey } = options;
	const inner = (value, path = []) => {
		if (!value || typeof value !== "object") return fn(value, path);
		if (stop?.(value, path)) return fn(value, path);
		const out = Array.isArray(value) ? [] : {};
		for (const prop in value) {
			if (!HAS_OWN.call(value, prop) || prop === "__proto__") continue;
			const key = getKey?.(prop, value[prop]) ?? prop;
			path.push(key);
			const next = inner(value[prop], path);
			path.pop();
			if (next != null) out[key] = next;
		}
		return out;
	};
	return inner(target);
}
function normalizeStyleObject(styles, context, shorthand) {
	const { utility, conditions } = context;
	const { hasShorthand, resolveShorthand } = utility;
	shorthand = shorthand !== false;
	return walkObject(styles, (value) => {
		if (Array.isArray(value)) return toResponsiveObject(value, conditions.breakpoints.keys);
		return value;
	}, {
		stop: Array.isArray,
		getKey: shorthand ? (prop) => hasShorthand ? resolveShorthand(prop) : prop : void 0
	});
}
function flatHashOrNull(args) {
	let h = 5381;
	for (let a = 0; a < args.length; a++) {
		const obj = args[a];
		if (obj === null || typeof obj !== "object") {
			h = h * 33 ^ 1;
			continue;
		}
		for (const k in obj) {
			const v = obj[k];
			const tv = typeof v;
			if (v !== null && tv === "object") return null;
			for (let i = 0; i < k.length; i++) h = h * 33 ^ k.charCodeAt(i);
			if (tv === "string") for (let i = 0; i < v.length; i++) h = h * 33 ^ v.charCodeAt(i);
			else if (tv === "number") h = h * 33 ^ (v | 0);
			else if (tv === "boolean") h = h * 33 ^ (v ? 991 : 997);
			else h = h * 33 ^ 2;
		}
	}
	return h >>> 0;
}
function flatArgsEqual(a, b) {
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i++) {
		const oa = a[i];
		const ob = b[i];
		if (oa === ob) continue;
		if (oa === null || ob === null || typeof oa !== "object" || typeof ob !== "object") return false;
		let n = 0;
		for (const k in oa) {
			if (oa[k] !== ob[k]) return false;
			n++;
		}
		if (n !== Object.keys(ob).length) return false;
	}
	return true;
}
function memo(fn) {
	const cache = /* @__PURE__ */ new Map();
	const stringCache = /* @__PURE__ */ new Map();
	const seen = /* @__PURE__ */ new WeakSet();
	const newNode = () => ({
		objects: /* @__PURE__ */ new WeakMap(),
		prims: /* @__PURE__ */ new Map(),
		out: void 0,
		has: false
	});
	const root = newNode();
	let lastHash;
	let lastKey;
	let lastValue;
	let hasLast = false;
	let misses = 0;
	const step = (node, v) => {
		if (v !== null && typeof v === "object") {
			let next = node.objects.get(v);
			if (next === void 0) {
				next = newNode();
				node.objects.set(v, next);
			}
			return next;
		}
		let next = node.prims.get(v);
		if (next === void 0) {
			if (node.prims.size > 64) node.prims.clear();
			next = newNode();
			node.prims.set(v, next);
		}
		return next;
	};
	const walk = (node, v) => {
		if (Array.isArray(v)) {
			node = step(node, "\0[");
			for (let i = 0; i < v.length; i++) node = walk(node, v[i]);
			return step(node, "\0]");
		}
		return step(node, v);
	};
	const readWalk = (node, v) => {
		if (node === void 0) return void 0;
		if (Array.isArray(v)) {
			node = node.prims.get("\0[");
			for (let i = 0; i < v.length && node !== void 0; i++) node = readWalk(node, v[i]);
			return node === void 0 ? void 0 : node.prims.get("\0]");
		}
		return v !== null && typeof v === "object" ? node.objects.get(v) : node.prims.get(v);
	};
	const markSeen = (v) => {
		if (Array.isArray(v)) {
			let all = true;
			for (let i = 0; i < v.length; i++) if (!markSeen(v[i])) all = false;
			return all;
		}
		if (v === null || typeof v !== "object") return true;
		if (seen.has(v)) return true;
		seen.add(v);
		return false;
	};
	return ((...args) => {
		let composed = false;
		for (let i = 0; i < args.length; i++) if (Array.isArray(args[i])) {
			composed = true;
			break;
		}
		if (composed) {
			let node = root;
			for (let i = 0; i < args.length && node !== void 0; i++) node = readWalk(node, args[i]);
			if (node !== void 0 && node.has) return node.out;
			const composedKey = JSON.stringify(args);
			let composedOut = hasLast && lastHash === void 0 && composedKey === lastKey ? lastValue : stringCache.get(composedKey);
			if (composedOut === void 0) {
				composedOut = fn(...args);
				stringCache.set(composedKey, composedOut);
				if (stringCache.size > 500) stringCache.delete(stringCache.keys().next().value);
			}
			let reused = false;
			if ((++misses & 3) === 0) {
				reused = true;
				for (let i = 0; i < args.length; i++) if (!markSeen(args[i])) reused = false;
			}
			if (reused) {
				let insert = root;
				for (let i = 0; i < args.length; i++) insert = walk(insert, args[i]);
				insert.out = composedOut;
				insert.has = true;
			}
			lastHash = void 0;
			lastKey = composedKey;
			lastValue = composedOut;
			hasLast = true;
			return composedOut;
		}
		const hash = flatHashOrNull(args);
		if (hash !== null) {
			if (hasLast && lastHash === hash && flatArgsEqual(args, lastKey)) return lastValue;
			let bucket = cache.get(hash);
			if (bucket) {
				for (let i = 0; i < bucket.length; i++) if (flatArgsEqual(args, bucket[i].args)) {
					lastHash = hash;
					lastKey = args;
					lastValue = bucket[i].out;
					hasLast = true;
					return bucket[i].out;
				}
			}
			const out = fn(...args);
			if (!bucket) {
				bucket = [];
				cache.set(hash, bucket);
			}
			bucket.push({
				args,
				out
			});
			if (bucket.length > 8) bucket.shift();
			if (cache.size > 500) cache.delete(cache.keys().next().value);
			lastHash = hash;
			lastKey = args;
			lastValue = out;
			hasLast = true;
			return out;
		}
		const key = JSON.stringify(args);
		if (hasLast && lastHash === void 0 && key === lastKey) return lastValue;
		const cached = stringCache.get(key);
		if (cached !== void 0) {
			lastHash = void 0;
			lastKey = key;
			lastValue = cached;
			hasLast = true;
			return cached;
		}
		const out = fn(...args);
		stringCache.set(key, out);
		if (stringCache.size > 500) stringCache.delete(stringCache.keys().next().value);
		lastHash = void 0;
		lastKey = key;
		lastValue = out;
		hasLast = true;
		return out;
	});
}
function weakMemo(fn) {
	const cache = /* @__PURE__ */ new WeakMap();
	return ((arg) => {
		if (!arg || typeof arg !== "object") return fn(arg);
		if (cache.has(arg)) return cache.get(arg);
		const out = fn(arg);
		cache.set(arg, out);
		return out;
	});
}
function mergeProps(...src) {
	const out = {};
	for (const obj of src) {
		if (!obj) continue;
		for (const k in obj) {
			if (!HAS_OWN.call(obj, k) || k === "__proto__" || k === "constructor" || k === "prototype") continue;
			const prev = out[k];
			const next = obj[k];
			out[k] = isObject(prev) && isObject(next) ? mergeProps(prev, next) : next;
		}
	}
	return out;
}
var WHITESPACE_REGEX = /[\n\s]+/g;
var sanitizeStyleValue = (value) => typeof value === "string" ? value.replace(WHITESPACE_REGEX, " ") : value;
function resolveStyleArgs(styles, context) {
	const out = [];
	const visit = (items) => {
		for (let i = 0; i < items.length; i++) {
			const style = items[i];
			if (Array.isArray(style)) {
				visit(style);
				continue;
			}
			if (!isObject(style)) continue;
			for (const key in style) if (style[key] !== void 0) {
				out.push(style);
				break;
			}
		}
	};
	visit(styles);
	if (out.length < 2) return out;
	for (let i = 0; i < out.length; i++) out[i] = normalizeStyleObject(out[i], context);
	return out;
}
function createSerializeCss(context) {
	const u = context.utility;
	const c = context.conditions;
	const hash = context.hash;
	const fmt = (s) => u.prefix ? u.prefix + "-" + s : s;
	const toClass = (paths, name) => {
		const parts = c.finalize(paths);
		parts.push(hash ? name : fmt(name));
		return hash ? fmt(u.toHash(parts, toHash)) : parts.join(":");
	};
	return weakMemo(memo(function serializeCss({ base, ...styles } = {}) {
		const obj = normalizeStyleObject(base ? Object.assign(styles, base) : styles, context);
		const set = /* @__PURE__ */ new Set();
		walkObject(obj, (value, paths) => {
			if (value == null) return;
			const important = isImportant(value);
			const [prop, ...all] = c.shift(paths);
			const cond = filterBaseConditions(all);
			const res = u.transform(prop, withoutSpace(withoutImportant(sanitizeStyleValue(value))));
			let name = toClass(cond, res.className);
			if (important) name += "!";
			set.add(name);
		});
		let out = "";
		for (const name of set) out += out ? " " + name : name;
		return out;
	}));
}
function createMergeCss(context) {
	return function mergeCss() {
		return mergeProps(...resolveStyleArgs(arguments, context));
	};
}
function createSerializeCssArgs(serializeCss, mergeCss) {
	return memo(function serializeCssArgs(...styles) {
		return serializeCss(mergeCss(...styles));
	});
}
var HYPHENATE_PROPERTY_REGEX = /[A-Z]/g;
var MS_PROPERTY_REGEX = /^ms-/;
function hypenateProperty(property) {
	return property.startsWith("--") ? property : property.replace(HYPHENATE_PROPERTY_REGEX, "-$&").replace(MS_PROPERTY_REGEX, "-ms-").toLowerCase();
}
function withoutSpace(str) {
	return typeof str === "string" && str.indexOf(" ") >= 0 ? str.replaceAll(" ", "_") : str;
}
var IMPORTANT_REGEX = /\s*!(important)?\s*$/i;
function isImportant(value) {
	return typeof value === "string" ? IMPORTANT_REGEX.test(value) : false;
}
function withoutImportant(value) {
	return typeof value === "string" ? value.replace(IMPORTANT_REGEX, "").trim() : value;
}
//#endregion
//#region ../../../core/styled-system/styled-system/css/conditions.js
var conditions = /* @__PURE__ */ new Set(/* @__PURE__ */ "2xl,2xlDown,2xlOnly,2xlToDesktop,2xlToTablet,2xlToXs,@/2xl,@/2xlDown,@/2xlOnly,@/2xlTo3xl,@/2xlTo4xl,@/2xlTo5xl,@/2xlTo6xl,@/2xlTo7xl,@/2xlTo8xl,@/2xs,@/2xsDown,@/2xsOnly,@/2xsTo2xl,@/2xsTo3xl,@/2xsTo4xl,@/2xsTo5xl,@/2xsTo6xl,@/2xsTo7xl,@/2xsTo8xl,@/2xsToLg,@/2xsToMd,@/2xsToSm,@/2xsToXl,@/2xsToXs,@/3xl,@/3xlDown,@/3xlOnly,@/3xlTo4xl,@/3xlTo5xl,@/3xlTo6xl,@/3xlTo7xl,@/3xlTo8xl,@/3xs,@/3xsDown,@/3xsOnly,@/3xsTo2xl,@/3xsTo2xs,@/3xsTo3xl,@/3xsTo4xl,@/3xsTo5xl,@/3xsTo6xl,@/3xsTo7xl,@/3xsTo8xl,@/3xsToLg,@/3xsToMd,@/3xsToSm,@/3xsToXl,@/3xsToXs,@/4xl,@/4xlDown,@/4xlOnly,@/4xlTo5xl,@/4xlTo6xl,@/4xlTo7xl,@/4xlTo8xl,@/5xl,@/5xlDown,@/5xlOnly,@/5xlTo6xl,@/5xlTo7xl,@/5xlTo8xl,@/6xl,@/6xlDown,@/6xlOnly,@/6xlTo7xl,@/6xlTo8xl,@/7xl,@/7xlDown,@/7xlOnly,@/7xlTo8xl,@/8xl,@/8xlDown,@/8xlOnly,@/lg,@/lgDown,@/lgOnly,@/lgTo2xl,@/lgTo3xl,@/lgTo4xl,@/lgTo5xl,@/lgTo6xl,@/lgTo7xl,@/lgTo8xl,@/lgToXl,@/md,@/mdDown,@/mdOnly,@/mdTo2xl,@/mdTo3xl,@/mdTo4xl,@/mdTo5xl,@/mdTo6xl,@/mdTo7xl,@/mdTo8xl,@/mdToLg,@/mdToXl,@/sm,@/smDown,@/smOnly,@/smTo2xl,@/smTo3xl,@/smTo4xl,@/smTo5xl,@/smTo6xl,@/smTo7xl,@/smTo8xl,@/smToLg,@/smToMd,@/smToXl,@/xl,@/xlDown,@/xlOnly,@/xlTo2xl,@/xlTo3xl,@/xlTo4xl,@/xlTo5xl,@/xlTo6xl,@/xlTo7xl,@/xlTo8xl,@/xs,@/xsDown,@/xsOnly,@/xsTo2xl,@/xsTo3xl,@/xsTo4xl,@/xsTo5xl,@/xsTo6xl,@/xsTo7xl,@/xsTo8xl,@/xsToLg,@/xsToMd,@/xsToSm,@/xsToXl,_active,_after,_anyPointerCoarse,_anyPointerFine,_anyPointerNone,_atValue,_autofill,_backdrop,_before,_checked,_closed,_complete,_current,_currentPage,_currentStep,_dark,_default,_disabled,_dragging,_empty,_enabled,_even,_expanded,_file,_first,_firstLetter,_firstLine,_firstOfType,_focus,_focusVisible,_focusWithin,_fullscreen,_grabbed,_groupActive,_groupChecked,_groupDisabled,_groupExpanded,_groupFocus,_groupFocusVisible,_groupFocusWithin,_groupHover,_groupInvalid,_hidden,_highContrast,_highlighted,_horizontal,_hover,_icon,_inRange,_incomplete,_indeterminate,_inert,_invalid,_invertedColors,_landscape,_last,_lastOfType,_lessContrast,_light,_loading,_ltr,_marker,_moreContrast,_motionReduce,_motionSafe,_noscript,_now,_odd,_only,_onlyOfType,_open,_optional,_osDark,_osLight,_outOfRange,_overValue,_peerActive,_peerChecked,_peerDisabled,_peerExpanded,_peerFocus,_peerFocusVisible,_peerFocusWithin,_peerHover,_peerInvalid,_peerPlaceholderShown,_placeholder,_placeholderShown,_pointerCoarse,_pointerFine,_pointerNone,_portrait,_pressed,_print,_rangeEnd,_rangeStart,_readOnly,_readWrite,_required,_rtl,_scrollbar,_scrollbarThumb,_scrollbarTrack,_selected,_selection,_starting,_target,_today,_topmost,_unavailable,_underValue,_userInvalid,_userValid,_valid,_vertical,_visited,base,desktop,desktopDown,desktopOnly,lg,lgDown,lgOnly,lgTo2xl,lgToDesktop,lgToTablet,lgToXl,lgToXs,md,mdDown,mdOnly,mdTo2xl,mdToDesktop,mdToLg,mdToTablet,mdToXl,mdToXs,sm,smDown,smOnly,smTo2xl,smToDesktop,smToLg,smToMd,smToTablet,smToXl,smToXs,tablet,tabletDown,tabletOnly,tabletToDesktop,xl,xlDown,xlOnly,xlTo2xl,xlToDesktop,xlToTablet,xlToXs,xs,xsDown,xsOnly,xsToDesktop,xsToTablet".split(","));
var conditionRe = /^@|&/;
var underscoreRe = /^_/;
var selectorRe = /&|@/;
var breakpointKeys = [
	"base",
	"sm",
	"md",
	"lg",
	"xl",
	"2xl",
	"xs",
	"tablet",
	"desktop"
];
function isCondition(v) {
	return conditions.has(v) || conditionRe.test(v);
}
function finalizeConditions(paths) {
	return paths.map((p) => {
		if (conditions.has(p)) return p.replace(underscoreRe, "");
		if (selectorRe.test(p)) return `[${withoutSpace(p.trim())}]`;
		return p;
	});
}
function sortConditions(paths) {
	return [...paths].sort((a, b) => {
		const aa = isCondition(a);
		const bb = isCondition(b);
		return aa && !bb ? 1 : !aa && bb ? -1 : 0;
	});
}
//#endregion
//#region ../../../core/styled-system/styled-system/css/css.js
var utilities = "WebkitTextFillColor:wktf-c,accentColor:ac-c,alignContent:ac,alignItems:ai,alignSelf:as,animation:anim,animationComposition:anim-comp,animationDelay:anim-dly,animationDirection:anim-dir,animationDuration:anim-dur,animationFillMode:anim-fm,animationIterationCount:anim-ic,animationName:anim-n,animationPlayState:anim-ps,animationRange:anim-r,animationRangeEnd:anim-re,animationRangeStart:anim-rs,animationState:anim-s,animationTimeline:anim-tl,animationTimingFunction:anim-tmf,appearance:ap,aspectRatio:asp,backdropBlur:bkdp-blur,backdropBrightness:bkdp-brightness,backdropContrast:bkdp-contrast,backdropFilter:bkdp,backdropGrayscale:bkdp-grayscale,backdropHueRotate:bkdp-hue-rotate,backdropInvert:bkdp-invert,backdropOpacity:bkdp-opacity,backdropSaturate:bkdp-saturate,backdropSepia:bkdp-sepia,backfaceVisibility:bfv,background:bg/1,backgroundAttachment:bg-a/bgAttachment,backgroundBlendMode:bg-bm/bgBlendMode,backgroundClip:bg-cp/bgClip,backgroundColor:bg-c/bgColor,backgroundConic:bg-conic/bgConic,backgroundGradient:bg-grad/bgGradient,backgroundImage:bg-i/bgImage,backgroundLinear:bg-linear/bgLinear,backgroundOrigin:bg-o/bgOrigin,backgroundPosition:bg-p/bgPosition,backgroundPositionX:bg-p-x/bgPositionX,backgroundPositionY:bg-p-y/bgPositionY,backgroundRadial:bg-radial/bgRadial,backgroundRepeat:bg-r/bgRepeat,backgroundSize:bg-s/bgSize,blockSize:h-bs,border:bd,borderBlock:bd-y/borderY,borderBlockColor:bd-y-c/borderYColor,borderBlockEnd:bd-be,borderBlockEndColor:bd-be-c,borderBlockEndWidth:bd-be-w,borderBlockStart:bd-bs,borderBlockStartColor:bd-bs-c,borderBlockStartWidth:bd-bs-w,borderBlockWidth:bd-y-w/borderYWidth,borderBottom:bd-b,borderBottomColor:bd-b-c,borderBottomLeftRadius:bdr-bl/roundedBottomLeft,borderBottomRadius:bdr-b/roundedBottom,borderBottomRightRadius:bdr-br/roundedBottomRight,borderBottomWidth:bd-b-w,borderCollapse:bd-cl,borderColor:bd-c,borderEndEndRadius:bdr-ee/roundedEndEnd,borderEndRadius:bdr-e/roundedEnd,borderEndStartRadius:bdr-es/roundedEndStart,borderInline:bd-x/borderX,borderInlineColor:bd-x-c/borderXColor,borderInlineEnd:bd-e/borderEnd,borderInlineEndColor:bd-e-c/borderEndColor,borderInlineEndWidth:bd-e-w/borderEndWidth,borderInlineStart:bd-s/borderStart,borderInlineStartColor:bd-s-c/borderStartColor,borderInlineStartWidth:bd-s-w/borderStartWidth,borderInlineWidth:bd-x-w/borderXWidth,borderLeft:bd-l,borderLeftColor:bd-l-c,borderLeftRadius:bdr-l/roundedLeft,borderLeftWidth:bd-l-w,borderRadius:bdr/rounded,borderRight:bd-r,borderRightColor:bd-r-c,borderRightRadius:bdr-r/roundedRight,borderRightWidth:bd-r-w,borderSpacing:bd-sp,borderSpacingX:bd-sx,borderSpacingY:bd-sy,borderStartEndRadius:bdr-se/roundedStartEnd,borderStartRadius:bdr-s/roundedStart,borderStartStartRadius:bdr-ss/roundedStartStart,borderTop:bd-t,borderTopColor:bd-t-c,borderTopLeftRadius:bdr-tl/roundedTopLeft,borderTopRadius:bdr-t/roundedTop,borderTopRightRadius:bdr-tr/roundedTopRight,borderTopWidth:bd-t-w,borderWidth:bd-w,boxDecorationBreak:bx-db,boxShadow:bx-sh/shadow,boxShadowColor:bx-sh-c/shadowColor,boxSize:size,boxSizing:bx-s,caretColor:ca-c,clipPath:cp-path,color:c,columnGap:cg,container:cq,containerName:cq-n,containerType:cq-t,display:d,divideColor:dvd-c,divideStyle:dvd-s,divideX:dvd-x,divideY:dvd-y,flexBasis:flex-b,flexDirection:flex-d/flexDir,flexGrow:flex-g,flexShrink:flex-sh,focusRingColor:focus-ring-c,focusRingOffset:focus-ring-o,focusRingStyle:focus-ring-s,focusRingWidth:focus-ring-w,focusVisibleRing:focus-v-ring,fontFamily:ff,fontFeatureSettings:ff-s,fontKerning:fk,fontPalette:fp,fontSize:fs,fontSizeAdjust:fs-a,fontSmoothing:fsmt,fontVariant:fv,fontVariantAlternates:fv-alt,fontVariantCaps:fv-caps,fontVariantNumeric:fv-num,fontVariationSettings:fv-s,fontWeight:fw,gradientFrom:grad-from,gradientFromPosition:grad-from-pos,gradientTo:grad-to,gradientToPosition:grad-to-pos,gradientVia:grad-via,gradientViaPosition:grad-via-pos,gridAutoColumns:grid-ac,gridAutoFlow:grid-af,gridAutoRows:grid-ar,gridColumn:grid-c,gridColumnEnd:grid-ce,gridColumnGap:grid-cg,gridColumnStart:grid-cs,gridGap:grid-g,gridRow:grid-r,gridRowGap:grid-rg,gridTemplateColumns:grid-tc,gridTemplateRows:grid-tr,height:h/1,hideBelow:show,hideFrom:hide,hyphens:hy,inlineSize:w-is,insetBlock:inset-y/insetY,insetBlockEnd:inset-be,insetBlockStart:inset-bs,insetInline:inset-x/insetX,insetInlineEnd:inset-e/end/insetEnd,insetInlineStart:inset-s/insetStart/start,justifyContent:jc,letterSpacing:ls,lineClamp:lc,lineHeight:lh,listStyle:li-s,listStyleImage:li-img,listStylePosition:li-pos,listStyleType:li-t,margin:m/1,marginBlock:my/marginY/1,marginBlockEnd:mbe,marginBlockStart:mbs,marginBottom:mb/1,marginInline:mx/marginX/1,marginInlineEnd:me/marginEnd/1,marginInlineStart:ms/marginStart/1,marginLeft:ml/1,marginRight:mr/1,marginTop:mt/1,mask:msk,maskBottomFrom:msk-b-from,maskBottomFromColor:msk-b-from-c,maskBottomTo:msk-b-to,maskBottomToColor:msk-b-to-c,maskClip:msk-cp,maskComposite:msk-cmp,maskConic:msk-conic,maskConicFrom:msk-conic-from,maskConicFromColor:msk-conic-from-c,maskConicTo:msk-conic-to,maskConicToColor:msk-conic-to-c,maskImage:msk-i,maskLeftFrom:msk-l-from,maskLeftFromColor:msk-l-from-c,maskLeftTo:msk-l-to,maskLeftToColor:msk-l-to-c,maskLinear:msk-linear,maskLinearFrom:msk-linear-from,maskLinearFromColor:msk-linear-from-c,maskLinearTo:msk-linear-to,maskLinearToColor:msk-linear-to-c,maskMode:msk-md,maskOrigin:msk-o,maskPosition:msk-p,maskRadial:msk-radial,maskRadialAt:msk-radial-at,maskRadialFrom:msk-radial-from,maskRadialFromColor:msk-radial-from-c,maskRadialShape:msk-radial-shape,maskRadialSize:msk-radial-sz,maskRadialTo:msk-radial-to,maskRadialToColor:msk-radial-to-c,maskRepeat:msk-r,maskRightFrom:msk-r-from,maskRightFromColor:msk-r-from-c,maskRightTo:msk-r-to,maskRightToColor:msk-r-to-c,maskSize:msk-s,maskTopFrom:msk-t-from,maskTopFromColor:msk-t-from-c,maskTopTo:msk-t-to,maskTopToColor:msk-t-to-c,maskType:msk-t,maskXFrom:msk-x-from,maskXFromColor:msk-x-from-c,maskXTo:msk-x-to,maskXToColor:msk-x-to-c,maskYFrom:msk-y-from,maskYFromColor:msk-y-from-c,maskYTo:msk-y-to,maskYToColor:msk-y-to-c,maxBlockSize:max-b,maxHeight:max-h/maxH,maxInlineSize:max-w-is,maxWidth:max-w/maxW,minBlockSize:min-h-bs,minHeight:min-h/minH,minInlineSize:min-w-is,minWidth:min-w/minW,mixBlendMode:mix-bm,objectFit:obj-f,objectPosition:obj-p,opacity:op,outline:ring/1,outlineColor:ring-c/ringColor,outlineOffset:ring-o/ringOffset,outlineWidth:ring-w/ringWidth,overflow:ov,overflowAnchor:ov-a,overflowBlock:ov-b,overflowClipBox:ovcp-bx,overflowClipMargin:ovcp-m,overflowInline:ov-i,overflowWrap:ov-wrap,overflowX:ov-x,overflowY:ov-y,overscrollBehavior:ovs-b,overscrollBehaviorBlock:ovs-bb,overscrollBehaviorInline:ovs-bi,overscrollBehaviorX:ovs-bx,overscrollBehaviorY:ovs-by,padding:p/1,paddingBlock:py/paddingY/1,paddingBlockEnd:pbe,paddingBlockStart:pbs,paddingBottom:pb/1,paddingInline:px/paddingX/1,paddingInlineEnd:pe/paddingEnd/1,paddingInlineStart:ps/paddingStart/1,paddingLeft:pl/1,paddingRight:pr/1,paddingTop:pt/1,position:pos/1,rowGap:rg,scrollBehavior:scr-bhv,scrollMargin:scr-m,scrollMarginBlock:scr-my/scrollMarginY,scrollMarginBlockEnd:scr-mbe,scrollMarginBlockStart:scr-mbt,scrollMarginBottom:scr-mb,scrollMarginInline:scr-mx/scrollMarginX,scrollMarginInlineEnd:scr-me,scrollMarginInlineStart:scr-ms,scrollMarginLeft:scr-ml,scrollMarginRight:scr-mr,scrollMarginTop:scr-mt,scrollPadding:scr-p,scrollPaddingBlock:scr-py/scrollPaddingY,scrollPaddingBlockEnd:scr-pbe,scrollPaddingBlockStart:scr-pbs,scrollPaddingBottom:scr-pb,scrollPaddingInline:scr-px/scrollPaddingX,scrollPaddingInlineEnd:scr-pe,scrollPaddingInlineStart:scr-ps,scrollPaddingLeft:scr-pl,scrollPaddingRight:scr-pr,scrollPaddingTop:scr-pt,scrollSnapAlign:scr-sa,scrollSnapCoordinate:scrs-c,scrollSnapDestination:scrs-d,scrollSnapMargin:scrs-m,scrollSnapMarginBottom:scrs-mb,scrollSnapMarginLeft:scrs-ml,scrollSnapMarginRight:scrs-mr,scrollSnapMarginTop:scrs-mt,scrollSnapPointsX:scrs-px,scrollSnapPointsY:scrs-py,scrollSnapStop:scrs-s,scrollSnapStrictness:scrs-strt,scrollSnapType:scrs-t,scrollSnapTypeX:scrs-tx,scrollSnapTypeY:scrs-ty,scrollTimeline:scrtl,scrollTimelineAxis:scrtl-a,scrollTimelineName:scrtl-n,scrollbar:scr-bar,scrollbarColor:scr-bar-c,scrollbarGutter:scr-bar-g,scrollbarThumb:scr-bar-th,scrollbarTrack:scr-bar-tk,scrollbarWidth:scr-bar-w,spaceX:sx,spaceY:sy,srOnly:sr,stroke:stk,strokeDasharray:stk-dsh,strokeDashoffset:stk-do,strokeLinecap:stk-lc,strokeLinejoin:stk-lj,strokeMiterlimit:stk-ml,strokeOpacity:stk-op,strokeWidth:stk-w,tableLayout:tbl,textAlign:ta,textDecoration:td,textDecorationColor:td-c,textDecorationStyle:td-s,textDecorationThickness:td-t,textEmphasisColor:te-c,textGradient:txt-grad,textIndent:ti,textOverflow:tov,textShadow:tsh,textShadowColor:tsh-c/textShadowColor,textSizeAdjust:txt-adj,textStyle:textStyle,textTransform:tt,textUnderlineOffset:tu-o,textWrap:tw,touchAction:tch-a,transform:trf,transformBox:trf-b,transformOrigin:trf-o,transformStyle:trf-s,transition:trs,transitionDelay:trs-dly,transitionDuration:trs-dur,transitionProperty:trs-prop,transitionTimingFunction:trs-tmf,translateX:/x,translateY:/y,translateZ:/z,truncate:trunc,userSelect:us,verticalAlign:va,visibility:vis,width:w/1,wordBreak:wb,zIndex:z";
function createCssContext() {
	const classNameByProp = /* @__PURE__ */ new Map();
	const shorthands = /* @__PURE__ */ new Map();
	utilities.split(",").forEach((utility) => {
		const [prop, meta] = utility.split(":");
		const [className, ...shorthandList] = meta.split("/");
		if (className) classNameByProp.set(prop, className);
		shorthandList.forEach((shorthand) => {
			const key = shorthand === "1" ? className : shorthand;
			shorthands.set(key, prop);
		});
	});
	const resolveShorthand = (prop) => shorthands.get(prop) || prop;
	return {
		hash: true,
		conditions: {
			shift: sortConditions,
			finalize: finalizeConditions,
			breakpoints: { keys: breakpointKeys }
		},
		utility: {
			prefix: null,
			hasShorthand: true,
			toHash(path, hashFn) {
				return hashFn(path.join(":"));
			},
			transform(prop, value) {
				const key = resolveShorthand(prop);
				return { className: `${classNameByProp.get(key) || hypenateProperty(key)}_${withoutSpace(value)}` };
			},
			resolveShorthand
		}
	};
}
var cssContext = /* @__PURE__ */ createCssContext();
var serializeCss = /* @__PURE__ */ createSerializeCss(cssContext);
var mergeCss = /* @__PURE__ */ createMergeCss(cssContext);
var serializeCssArgs = /* @__PURE__ */ createSerializeCssArgs(serializeCss, mergeCss);
var css = /* @__PURE__ */ Object.assign(function css(...styles) {
	if (styles.length === 1 && isObject(styles[0])) return serializeCss(styles[0]);
	return serializeCssArgs(...styles);
}, { raw: function cssRaw(...styles) {
	return mergeCss(...styles);
} });
//#endregion
//#region src/consultancy-requests.tsx
var consultancy_requests_exports = /* @__PURE__ */ __exportAll({ ConsultancyRequests: () => ConsultancyRequests });
/** Everything the browser will put focus on, in document order, inside the dialog. */
var FOCUSABLE_IN_DIALOG = "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex=\"-1\"])";
/**
* The amount in the currency it was priced in, not in the one the reader is browsing.
*
* `Intl` supplies the symbol, so no lookup against the page's currency is needed — which is
* the point: the consultant reading the list may well be in a different currency than the
* booker was priced in, and pairing the two would show a number labelled with the wrong one.
*/
var formatPrice = (price, currency, locale) => {
	if (price === void 0 || price === null) return "";
	const options = {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2
	};
	if (currency) try {
		return price.toLocaleString(locale, {
			...options,
			style: "currency",
			currency: currency.toUpperCase(),
			currencyDisplay: "symbol"
		});
	} catch {}
	return price.toLocaleString(locale, options);
};
var getCsrfTokenFromCookie = () => {
	if (typeof document === "undefined") return null;
	const token = document.cookie.split("; ").find((cookie) => cookie.startsWith("XSRF-TOKEN="));
	return token ? decodeURIComponent(token.slice(11)) : null;
};
var TAB_GROUPS = [
	"to-confirm",
	"scheduled",
	"completed",
	"archived"
];
/** Preview rows per group on the "Tutte" tab. */
var PREVIEW_LIMITS = {
	"to-confirm": 3,
	scheduled: 3,
	completed: 3,
	archived: 3
};
var COUNT_KEYS = {
	"to-confirm": "toConfirm",
	scheduled: "scheduled",
	completed: "completed",
	archived: "archived"
};
var TAB_TITLES = {
	"to-confirm": "consultancies.requests_tab_to_confirm",
	scheduled: "consultancies.requests_tab_scheduled",
	completed: "consultancies.requests_tab_completed",
	archived: "consultancies.requests_tab_archived"
};
/**
* Reads the tab from the listing query state. The historical `confirmed` union
* has no tab of its own and opens the closest one; anything unrecognised —
* including no filter at all — is the "Tutte" overview.
*/
var statusToTab = (status) => {
	if (status === "to-confirm" || status === "scheduled" || status === "completed" || status === "archived") return status;
	return status === "confirmed" ? "scheduled" : "all";
};
/**
* Calendar parts in the visitor's timezone, `null` on anything `Date` refuses.
* `Intl` keeps the server out of the markup: UTC is rendered until hydration
* identifies the visitor's zone, exactly like the previous rendering did.
*/
var getDateParts = (value, locale, timeZone) => {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return null;
	const zone = timeZone ?? "UTC";
	const parts = new Intl.DateTimeFormat(locale, {
		weekday: "short",
		day: "numeric",
		month: "short",
		hour: "2-digit",
		minute: "2-digit",
		hourCycle: "h23",
		timeZone: zone
	}).formatToParts(date);
	const get = (type) => parts.find((part) => part.type === type)?.value ?? "";
	return {
		weekday: get("weekday"),
		day: get("day"),
		month: get("month"),
		hour: get("hour"),
		minute: get("minute"),
		dayKey: new Intl.DateTimeFormat("en-CA", {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			timeZone: zone
		}).format(date)
	};
};
var capitalize = (value) => value === "" ? value : value.charAt(0).toUpperCase() + value.slice(1);
/**
* "Oggi · 19:22" for today, "Ven 25 set · 11:00" otherwise — the short label
* every date column, slot chip and countdown badge is built from. Without the
* weekday it reads "22 set · 10:00", the Data / Ora column shape.
*/
var formatShortDate = (value, locale, timeZone, todayLabel, weekday = true) => {
	const parts = getDateParts(value, locale, timeZone);
	if (!parts) return "";
	const todayKey = new Intl.DateTimeFormat("en-CA", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		timeZone: timeZone ?? "UTC"
	}).format(/* @__PURE__ */ new Date());
	const time = `${parts.hour}:${parts.minute}`;
	if (parts.dayKey === todayKey) return `${todayLabel} · ${time}`;
	return weekday ? `${capitalize(parts.weekday)} ${parts.day} ${parts.month} · ${time}` : `${parts.day} ${parts.month} · ${time}`;
};
/** Date-only shape of the archived column: "31 dic 2025". */
var formatDateOnly = (value, locale, timeZone) => {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "";
	return new Intl.DateTimeFormat(locale, {
		day: "numeric",
		month: "short",
		year: "numeric",
		timeZone: timeZone ?? "UTC"
	}).format(date);
};
var BoardContext = createContext(null);
var useBoard = () => {
	const value = useContext(BoardContext);
	if (!value) throw new Error("Board cells must render inside the requests board.");
	return value;
};
/**
* Static pill classes: one literal `css()` call each. Colours must never travel
* through a helper parameter — the styles are extracted at build time and a
* runtime-assembled object silently drops the rules.
*
* The radius is a fixed `xl` (~12px on a 25px pill) rather than `full`: a single
* line still reads as a pill. Table pills never wrap — the column widens to fit —
* while the card badges below may wrap at spaces, never mid-word.
*/
var pillGrey = css({
	alignItems: "center",
	alignSelf: "flex-start",
	backgroundColor: "grey.100",
	borderRadius: "xl",
	color: "text.500",
	display: "inline-flex",
	fontSize: "sm",
	gap: "1",
	paddingX: "2",
	paddingY: "0.5",
	whiteSpace: "nowrap",
	width: "fit"
});
var pillWarning = css({
	alignItems: "center",
	alignSelf: "flex-start",
	backgroundColor: "warning.500/20",
	borderRadius: "xl",
	color: "warning.700",
	display: "inline-flex",
	fontSize: "sm",
	gap: "1",
	paddingX: "2",
	paddingY: "0.5",
	whiteSpace: "nowrap",
	width: "fit"
});
var pillError = css({
	alignItems: "center",
	alignSelf: "flex-start",
	backgroundColor: "error.700/20",
	borderRadius: "xl",
	color: "error.700",
	display: "inline-flex",
	fontSize: "sm",
	gap: "1",
	paddingX: "2",
	paddingY: "0.5",
	whiteSpace: "nowrap",
	width: "fit"
});
var pillValid = css({
	alignItems: "center",
	alignSelf: "flex-start",
	backgroundColor: "valid.500/20",
	borderRadius: "xl",
	color: "valid.700",
	display: "inline-flex",
	fontSize: "sm",
	gap: "1",
	paddingX: "2",
	paddingY: "0.5",
	whiteSpace: "nowrap",
	width: "fit"
});
/** Keeps short status labels on a single line inside the wrappable pills. */
var singleLine = css({ whiteSpace: "nowrap" });
/**
* Desktop column widths: every column shrinks to its content, except
* "Richiesta" which absorbs the remaining space. Only precomputed class names
* travel through `columnDef.meta`, so each `css()` call below stays static.
*/
var fitColumn = css({
	whiteSpace: "nowrap",
	width: "1px"
});
var fluidColumn = css({ width: "100%" });
var fitColumnMeta = {
	thClassName: fitColumn,
	tdClassName: fitColumn
};
var fluidColumnMeta = {
	thClassName: fluidColumn,
	tdClassName: fluidColumn
};
/**
* Short date: "Oggi · 19:22", "Ven 25 set · 11:00" without a weekday,
* "31 dic 2025" date-only. Medium (500) unless told otherwise.
*/
function DateLabel({ value, weekday = true, bold = true, dateOnly = false }) {
	const { t } = useTranslation("core");
	const { locale } = usePage();
	const { timeZone } = useBoard();
	const label = dateOnly ? formatDateOnly(value, locale, timeZone) : formatShortDate(value, locale, timeZone, t("consultancies.requests_today"), weekday);
	if (label === "") return /* @__PURE__ */ jsx("span", {
		className: css({ color: "text.300" }),
		children: "—"
	});
	return bold ? /* @__PURE__ */ jsx("span", {
		className: css({ fontWeight: "medium" }),
		children: label
	}) : /* @__PURE__ */ jsx("span", { children: label });
}
/** Countdown to the request deadline. */
function ExpiryBadge({ value }) {
	const { t } = useTranslation("core");
	const { locale } = usePage();
	if (!value) return null;
	const target = new Date(value);
	const targetTime = target.getTime();
	if (Number.isNaN(targetTime)) return null;
	const diffMs = targetTime - Date.now();
	const days = Math.floor(diffMs / 864e5);
	let tone = pillError;
	if (days >= 3) tone = pillGrey;
	else if (days >= 1) tone = pillWarning;
	if (diffMs <= 0) return /* @__PURE__ */ jsxs("span", {
		className: pillError,
		children: [/* @__PURE__ */ jsx(FontAwesome, {
			name: "fa-sharp-regular fa-clock",
			size: .75
		}), t("consultancies.requests_status_expired")]
	});
	return /* @__PURE__ */ jsxs("span", {
		className: tone,
		children: [/* @__PURE__ */ jsx(FontAwesome, {
			name: "fa-sharp-regular fa-clock",
			size: .75
		}), t("consultancies.requests_expires_relative", { relative: intlFormatDistance(target, /* @__PURE__ */ new Date(), {
			locale,
			numeric: "auto"
		}) })]
	});
}
/** Countdown to the agreed appointment. */
function AppointmentBadge({ value }) {
	const { t } = useTranslation("core");
	const { locale } = usePage();
	const { timeZone } = useBoard();
	const target = new Date(value);
	const targetTime = target.getTime();
	if (Number.isNaN(targetTime)) return null;
	const diffMs = targetTime - Date.now();
	const tone = diffMs > 0 && diffMs < 108e5 ? pillWarning : pillGrey;
	if (diffMs <= 0) return /* @__PURE__ */ jsxs("span", {
		className: tone,
		children: [/* @__PURE__ */ jsx(FontAwesome, {
			name: "fa-sharp-regular fa-clock",
			size: .75
		}), formatShortDate(value, locale, timeZone, t("consultancies.requests_today"))]
	});
	return /* @__PURE__ */ jsxs("span", {
		className: tone,
		children: [/* @__PURE__ */ jsx(FontAwesome, {
			name: "fa-sharp-regular fa-clock",
			size: .75
		}), intlFormatDistance(target, /* @__PURE__ */ new Date(), {
			locale,
			numeric: "auto"
		})]
	});
}
/** The request text, truncated to three lines. */
function RequestCell({ request, card = false }) {
	const className = card ? css({
		color: "text.500",
		lineClamp: 3
	}) : css({
		color: "text.300",
		lineClamp: 3
	});
	return request.title ? /* @__PURE__ */ jsx("span", {
		className,
		children: request.title
	}) : /* @__PURE__ */ jsx("span", {
		className,
		children: "—"
	});
}
/** Truncates overflowing contact text with an ellipsis. */
var truncate = css({
	overflow: "hidden",
	textOverflow: "ellipsis",
	whiteSpace: "nowrap"
});
/**
* Three rows: the booker's name, the envelope with a `mailto:` e-mail and the
* phone with a `tel:` link — the consultant scans the worklist to find who to call.
*/
function CustomerCell({ request, card = false }) {
	return /* @__PURE__ */ jsxs("div", {
		className: css({
			display: "flex",
			flexDir: "column",
			gap: "1"
		}),
		children: [
			/* @__PURE__ */ jsx("span", {
				className: card ? css({
					color: "text.500",
					fontSize: "sm",
					fontWeight: "medium"
				}) : css({
					color: "text.300",
					fontSize: "sm",
					fontWeight: "medium",
					whiteSpace: "nowrap"
				}),
				children: request.name ?? ""
			}),
			request.email ? /* @__PURE__ */ jsxs("a", {
				href: `mailto:${request.email}`,
				title: request.email,
				className: card ? css({
					alignItems: "center",
					color: "accent.500",
					display: "flex",
					fontSize: "sm",
					gap: "1.5",
					maxWidth: "full",
					textDecoration: "underline",
					whiteSpace: "nowrap"
				}) : css({
					alignItems: "center",
					color: "accent.500",
					display: "flex",
					fontSize: "sm",
					gap: "1.5",
					maxWidth: "56",
					textDecoration: "underline",
					whiteSpace: "nowrap"
				}),
				children: [/* @__PURE__ */ jsx(FontAwesome, {
					name: "fa-sharp-regular fa-envelope",
					size: .75,
					css: { flexShrink: 0 }
				}), /* @__PURE__ */ jsx("span", {
					className: truncate,
					children: request.email
				})]
			}) : null,
			request.phone ? /* @__PURE__ */ jsxs("a", {
				href: `tel:${request.phone.replace(/\s/g, "")}`,
				title: request.phone,
				className: card ? css({
					alignItems: "center",
					color: "accent.500",
					display: "flex",
					fontSize: "sm",
					gap: "1.5",
					maxWidth: "full",
					textDecoration: "underline",
					whiteSpace: "nowrap"
				}) : css({
					alignItems: "center",
					color: "accent.500",
					display: "flex",
					fontSize: "sm",
					gap: "1.5",
					textDecoration: "underline",
					whiteSpace: "nowrap"
				}),
				children: [/* @__PURE__ */ jsx(FontAwesome, {
					name: "fa-sharp-regular fa-phone",
					size: .75,
					css: { flexShrink: 0 }
				}), /* @__PURE__ */ jsx("span", {
					className: truncate,
					children: request.phone
				})]
			}) : null
		]
	});
}
/** "1 ora", "3 ore" — the count picks the singular. */
function DurationCell({ hours }) {
	const { t } = useTranslation("core");
	return /* @__PURE__ */ jsx("span", {
		className: css({
			color: "text.300",
			whiteSpace: "nowrap"
		}),
		children: hours === 1 ? t("consultancies.requests_duration_one") : t("consultancies.requests_duration_other", { count: hours })
	});
}
function PriceCell({ request }) {
	const { locale } = usePage();
	const isEstimate = request.price === null && request.estimatedPrice !== null && request.estimatedCurrency !== null;
	const price = isEstimate ? request.estimatedPrice : request.price;
	const currency = isEstimate ? request.estimatedCurrency : request.currency;
	if (price === null) return /* @__PURE__ */ jsx("span", {
		className: css({ color: "text.300" }),
		children: "—"
	});
	return /* @__PURE__ */ jsx("span", {
		className: css({
			color: "text.300",
			whiteSpace: "nowrap"
		}),
		children: formatPrice(price, currency, locale)
	});
}
/** Proposed times, "Ven 25 set · 11:00", one per line. */
function SlotsCell({ request, card = false }) {
	const { t } = useTranslation("core");
	const { locale } = usePage();
	const { timeZone } = useBoard();
	const slotClassName = card ? css({
		color: "text.500",
		fontSize: "sm",
		whiteSpace: "nowrap"
	}) : css({
		color: "text.300",
		fontSize: "sm",
		whiteSpace: "nowrap"
	});
	if (!request.slotProposals.length) return /* @__PURE__ */ jsx("span", {
		className: slotClassName,
		children: "—"
	});
	return /* @__PURE__ */ jsx("ul", {
		className: css({
			display: "flex",
			flexDir: "column",
			gap: "1",
			listStyle: "none"
		}),
		children: request.slotProposals.map((slot) => /* @__PURE__ */ jsx("li", {
			className: slotClassName,
			children: formatShortDate(slot, locale, timeZone, t("consultancies.requests_today"))
		}, slot))
	});
}
/** "Pagata" green, "Da pagare" yellow — read off the raw status. */
function PaymentStateBadge({ request }) {
	const { t } = useTranslation("core");
	return request.status === "paid" ? /* @__PURE__ */ jsx("span", {
		className: pillValid,
		children: /* @__PURE__ */ jsx("span", {
			className: singleLine,
			children: t("consultancies.requests_status_paid")
		})
	}) : /* @__PURE__ */ jsx("span", {
		className: pillWarning,
		children: /* @__PURE__ */ jsx("span", {
			className: singleLine,
			children: t("consultancies.requests_payment_pending")
		})
	});
}
/** "Scaduta" yellow, "Rifiutata" red. */
function ArchivedStateBadge({ request }) {
	const { t } = useTranslation("core");
	return request.status === "expired" ? /* @__PURE__ */ jsx("span", {
		className: pillWarning,
		children: /* @__PURE__ */ jsx("span", {
			className: singleLine,
			children: t("consultancies.requests_status_expired")
		})
	}) : /* @__PURE__ */ jsx("span", {
		className: pillError,
		children: /* @__PURE__ */ jsx("span", {
			className: singleLine,
			children: t("consultancies.requests_status_rejected")
		})
	});
}
function ConfirmSquareButton({ request }) {
	const { t } = useTranslation("core");
	const { submittingId, onConfirm } = useBoard();
	return /* @__PURE__ */ jsx(Button, {
		size: "sm",
		variant: "solid",
		color: "primary",
		icon: "fa-sharp-regular fa-check",
		title: t("consultancies.requests_confirm"),
		disabled: submittingId === request.id,
		onClick: () => {
			onConfirm(request);
		}
	});
}
function RejectSquareButton({ request }) {
	const { t } = useTranslation("core");
	const { submittingId, onReject } = useBoard();
	return /* @__PURE__ */ jsx(Button, {
		size: "sm",
		variant: "solid",
		color: "error",
		icon: "fa-sharp-regular fa-xmark",
		title: t("consultancies.requests_reject_action"),
		disabled: submittingId === request.id,
		css: {
			backgroundColor: "error.700/20",
			color: "error.700",
			_hover: { backgroundColor: "error.700/20" },
			_active: { backgroundColor: "error.700/20" }
		},
		onClick: () => {
			onReject(request);
		}
	});
}
function ToConfirmActions({ request }) {
	return /* @__PURE__ */ jsxs("div", {
		className: css({
			display: "flex",
			gap: "2"
		}),
		children: [/* @__PURE__ */ jsx(ConfirmSquareButton, { request }), /* @__PURE__ */ jsx(RejectSquareButton, { request })]
	});
}
/** Re-proposes a rejected or expired request: same confirmation dialog. */
function RepeatSquareButton({ request }) {
	const { t } = useTranslation("core");
	const { submittingId, onConfirm } = useBoard();
	return /* @__PURE__ */ jsx(Button, {
		size: "sm",
		variant: "solid",
		color: "primary",
		icon: "fa-sharp-regular fa-rotate-right",
		title: t("consultancies.requests_repropose"),
		disabled: submittingId === request.id,
		onClick: () => {
			onConfirm(request);
		}
	});
}
function ScheduledActions({ request }) {
	const { t } = useTranslation("core");
	const { copiedPaymentUrl, onCopyPayment } = useBoard();
	if (request.status === "paid") return request.meetingUrl ? /* @__PURE__ */ jsx(Button, {
		size: "sm",
		variant: "solid",
		color: "primary",
		icon: "fa-sharp-regular fa-video",
		href: request.meetingUrl,
		target: "_blank",
		children: t("consultancies.requests_start_video")
	}) : null;
	const paymentUrl = request.paymentUrl;
	const copied = copiedPaymentUrl === request.paymentUrl;
	return paymentUrl ? /* @__PURE__ */ jsx(Button, {
		size: "sm",
		variant: "transparent",
		color: "primary",
		icon: copied ? "fa-sharp-regular fa-check" : "fa-sharp-regular fa-copy",
		onClick: () => {
			onCopyPayment(paymentUrl);
		},
		children: copied ? t("consultancies.requests_copied") : t("consultancies.requests_copy_payment")
	}) : null;
}
var helper = createDataTableColumnHelper();
var useGroupColumns = (group, sortable) => {
	const { t } = useTranslation("core");
	return useMemo(() => {
		const requestColumn = helper.display({
			id: "request",
			header: t("consultancies.requests_detail"),
			meta: fluidColumnMeta,
			cell: ({ row }) => /* @__PURE__ */ jsx(RequestCell, { request: row.original })
		});
		const customerColumn = helper.display({
			id: "booker",
			header: t("consultancies.requests_customer"),
			enableSorting: false,
			meta: fitColumnMeta,
			cell: ({ row }) => /* @__PURE__ */ jsx(CustomerCell, { request: row.original })
		});
		const durationColumn = helper.display({
			id: "duration",
			header: t("consultancies.requests_duration"),
			enableSorting: false,
			meta: fitColumnMeta,
			cell: ({ row }) => /* @__PURE__ */ jsx(DurationCell, { hours: row.original.hours })
		});
		const priceColumn = helper.accessor("price", {
			id: "price",
			header: t("consultancies.requests_price"),
			enableSorting: sortable,
			meta: fitColumnMeta,
			cell: ({ row }) => /* @__PURE__ */ jsx(PriceCell, { request: row.original })
		});
		const slotsColumn = helper.display({
			id: "slots",
			header: t("consultancies.requests_slots"),
			enableSorting: false,
			meta: fitColumnMeta,
			cell: ({ row }) => /* @__PURE__ */ jsx(SlotsCell, { request: row.original })
		});
		switch (group) {
			case "to-confirm": return helper.columns([
				helper.accessor("createdAt", {
					id: "created_at",
					header: t("consultancies.requests_datetime"),
					enableSorting: sortable,
					meta: fitColumnMeta,
					cell: ({ row }) => /* @__PURE__ */ jsxs("div", {
						className: css({
							display: "flex",
							flexDir: "column",
							gap: "1",
							whiteSpace: "nowrap"
						}),
						children: [/* @__PURE__ */ jsx(DateLabel, {
							value: row.original.createdAt,
							weekday: false
						}), /* @__PURE__ */ jsx(ExpiryBadge, { value: row.original.expiresAt })]
					})
				}),
				requestColumn,
				customerColumn,
				durationColumn,
				priceColumn,
				slotsColumn,
				helper.display({
					id: "actions",
					header: "",
					enableSorting: false,
					meta: fitColumnMeta,
					cell: ({ row }) => /* @__PURE__ */ jsx(ToConfirmActions, { request: row.original })
				})
			]);
			case "scheduled": return helper.columns([
				helper.accessor("scheduledAt", {
					id: "scheduled_at",
					header: t("consultancies.requests_scheduled_at"),
					enableSorting: sortable,
					meta: fitColumnMeta,
					cell: ({ row }) => row.original.scheduledAt ? /* @__PURE__ */ jsxs("div", {
						className: css({
							display: "flex",
							flexDir: "column",
							gap: "1",
							whiteSpace: "nowrap"
						}),
						children: [/* @__PURE__ */ jsx(DateLabel, { value: row.original.scheduledAt }), /* @__PURE__ */ jsx(AppointmentBadge, { value: row.original.scheduledAt })]
					}) : /* @__PURE__ */ jsx("span", {
						className: css({ color: "text.300" }),
						children: "—"
					})
				}),
				requestColumn,
				customerColumn,
				durationColumn,
				priceColumn,
				helper.accessor("status", {
					id: "status",
					header: t("consultancies.requests_status"),
					enableSorting: sortable,
					meta: fitColumnMeta,
					cell: ({ row }) => /* @__PURE__ */ jsx(PaymentStateBadge, { request: row.original })
				}),
				helper.display({
					id: "actions",
					header: "",
					enableSorting: false,
					meta: fitColumnMeta,
					cell: ({ row }) => /* @__PURE__ */ jsx(ScheduledActions, { request: row.original })
				})
			]);
			case "completed": return helper.columns([
				helper.accessor("scheduledAt", {
					id: "scheduled_at",
					header: t("consultancies.requests_scheduled_at"),
					enableSorting: sortable,
					meta: fitColumnMeta,
					cell: ({ row }) => row.original.scheduledAt ? /* @__PURE__ */ jsx("span", {
						className: css({ whiteSpace: "nowrap" }),
						children: /* @__PURE__ */ jsx(DateLabel, { value: row.original.scheduledAt })
					}) : /* @__PURE__ */ jsx("span", {
						className: css({ color: "text.300" }),
						children: "—"
					})
				}),
				requestColumn,
				customerColumn,
				durationColumn,
				priceColumn
			]);
			case "archived": return helper.columns([
				helper.accessor("createdAt", {
					id: "created_at",
					header: t("consultancies.requests_datetime"),
					enableSorting: sortable,
					meta: fitColumnMeta,
					cell: ({ row }) => /* @__PURE__ */ jsx("span", {
						className: css({ whiteSpace: "nowrap" }),
						children: /* @__PURE__ */ jsx(DateLabel, { value: row.original.createdAt })
					})
				}),
				requestColumn,
				customerColumn,
				durationColumn,
				priceColumn,
				slotsColumn,
				helper.accessor("status", {
					id: "status",
					header: t("consultancies.requests_status"),
					enableSorting: sortable,
					meta: fitColumnMeta,
					cell: ({ row }) => /* @__PURE__ */ jsx(ArchivedStateBadge, { request: row.original })
				}),
				helper.display({
					id: "actions",
					header: "",
					enableSorting: false,
					meta: fitColumnMeta,
					cell: ({ row }) => /* @__PURE__ */ jsx(RepeatSquareButton, { request: row.original })
				})
			]);
		}
	}, [
		group,
		sortable,
		t
	]);
};
/** The card's right-aligned date and time. */
function CardDate({ request, group }) {
	const value = group === "scheduled" || group === "completed" ? request.scheduledAt : request.createdAt;
	if (!value) return /* @__PURE__ */ jsx("span", {
		className: css({
			color: "text.300",
			fontSize: "sm",
			marginLeft: "auto"
		}),
		children: "—"
	});
	return /* @__PURE__ */ jsx("span", {
		className: css({
			color: "text.500",
			fontSize: "sm",
			fontWeight: "medium",
			marginLeft: "auto",
			whiteSpace: "nowrap"
		}),
		children: /* @__PURE__ */ jsx(DateLabel, {
			value,
			bold: false
		})
	});
}
var cardDataBadgeGrey = css({
	alignItems: "center",
	alignSelf: "flex-start",
	backgroundColor: "grey.100",
	borderRadius: "sm",
	color: "text.500",
	display: "inline-flex",
	fontSize: "sm",
	fontStyle: "italic",
	maxWidth: "48",
	paddingX: "2",
	paddingY: "0.5",
	whiteSpace: "normal",
	width: "fit"
});
var cardDataBadgeWarning = css({
	alignItems: "center",
	alignSelf: "flex-start",
	backgroundColor: "warning.500/20",
	borderRadius: "sm",
	color: "warning.700",
	display: "inline-flex",
	fontSize: "sm",
	fontStyle: "italic",
	maxWidth: "48",
	paddingX: "2",
	paddingY: "0.5",
	whiteSpace: "normal",
	width: "fit"
});
var cardDataBadgeValid = css({
	alignItems: "center",
	alignSelf: "flex-start",
	backgroundColor: "valid.500/20",
	borderRadius: "sm",
	color: "valid.700",
	display: "inline-flex",
	fontSize: "sm",
	fontStyle: "italic",
	maxWidth: "48",
	paddingX: "2",
	paddingY: "0.5",
	whiteSpace: "normal",
	width: "fit"
});
var cardCompletedBadge = css({
	alignItems: "center",
	alignSelf: "flex-start",
	backgroundColor: "grey.50",
	borderRadius: "xl",
	color: "text.300",
	display: "inline-flex",
	fontSize: "sm",
	maxWidth: "48",
	paddingX: "2",
	paddingY: "0.5",
	whiteSpace: "normal",
	width: "fit"
});
function CardCompletedBadge() {
	const { t } = useTranslation("core");
	return /* @__PURE__ */ jsx("span", {
		className: cardCompletedBadge,
		children: /* @__PURE__ */ jsx("span", {
			className: singleLine,
			children: t("consultancies.requests_status_completed")
		})
	});
}
function CardStatusBadge({ request, group }) {
	if (group === "to-confirm") return /* @__PURE__ */ jsx(ExpiryBadge, { value: request.expiresAt });
	if (group === "scheduled" && request.scheduledAt) return /* @__PURE__ */ jsx(AppointmentBadge, { value: request.scheduledAt });
	if (group === "completed") return /* @__PURE__ */ jsx(CardCompletedBadge, {});
	return group === "archived" ? /* @__PURE__ */ jsx(ArchivedStateBadge, { request }) : null;
}
function CardPaymentStateBadge({ request }) {
	const { t } = useTranslation("core");
	return request.status === "paid" ? /* @__PURE__ */ jsx("span", {
		className: cardDataBadgeValid,
		children: /* @__PURE__ */ jsx("span", {
			className: singleLine,
			children: t("consultancies.requests_status_paid")
		})
	}) : /* @__PURE__ */ jsx("span", {
		className: cardDataBadgeWarning,
		children: /* @__PURE__ */ jsx("span", {
			className: singleLine,
			children: t("consultancies.requests_payment_pending")
		})
	});
}
function CardDurationBadge({ hours }) {
	const { t } = useTranslation("core");
	return /* @__PURE__ */ jsxs("span", {
		className: cardDataBadgeGrey,
		children: [
			t("consultancies.requests_duration"),
			":\xA0",
			hours === 1 ? t("consultancies.requests_duration_one") : t("consultancies.requests_duration_other", { count: hours })
		]
	});
}
function CardPriceBadge({ request }) {
	const { t } = useTranslation("core");
	const { locale } = usePage();
	const isEstimate = request.price === null && request.estimatedPrice !== null && request.estimatedCurrency !== null;
	const price = isEstimate ? request.estimatedPrice : request.price;
	const currency = isEstimate ? request.estimatedCurrency : request.currency;
	return /* @__PURE__ */ jsxs("span", {
		className: cardDataBadgeGrey,
		children: [
			t("consultancies.requests_price"),
			":\xA0",
			price === null ? "—" : formatPrice(price, currency, locale)
		]
	});
}
function CardDetails({ request, group }) {
	return /* @__PURE__ */ jsxs("div", {
		className: css({
			display: "flex",
			flexWrap: "wrap",
			gap: "1.5"
		}),
		children: [
			group === "scheduled" ? /* @__PURE__ */ jsx(CardPaymentStateBadge, { request }) : null,
			/* @__PURE__ */ jsx(CardDurationBadge, { hours: request.hours }),
			/* @__PURE__ */ jsx(CardPriceBadge, { request })
		]
	});
}
function CardToConfirmActions({ request }) {
	const { t } = useTranslation("core");
	const { submittingId, onConfirm, onReject } = useBoard();
	return /* @__PURE__ */ jsxs("div", {
		className: css({
			display: "flex",
			gap: "2"
		}),
		children: [/* @__PURE__ */ jsx("div", {
			className: css({ flex: "1" }),
			children: /* @__PURE__ */ jsx(Button, {
				width: "full",
				size: "sm",
				variant: "solid",
				color: "primary",
				icon: "fa-sharp-regular fa-check",
				disabled: submittingId === request.id,
				onClick: () => {
					onConfirm(request);
				},
				children: t("consultancies.requests_confirm")
			})
		}), /* @__PURE__ */ jsx("div", {
			className: css({ flex: "1" }),
			children: /* @__PURE__ */ jsx(Button, {
				width: "full",
				size: "sm",
				variant: "solid",
				color: "error",
				icon: "fa-sharp-regular fa-xmark",
				disabled: submittingId === request.id,
				css: {
					backgroundColor: "error.700/20",
					color: "error.700",
					_hover: { backgroundColor: "error.700/20" },
					_active: { backgroundColor: "error.700/20" }
				},
				onClick: () => {
					onReject(request);
				},
				children: t("consultancies.requests_reject_action")
			})
		})]
	});
}
function CardScheduledActions({ request }) {
	const { t } = useTranslation("core");
	const { copiedPaymentUrl, onCopyPayment } = useBoard();
	if (request.status === "paid") return request.meetingUrl ? /* @__PURE__ */ jsx(Button, {
		width: "full",
		size: "sm",
		variant: "solid",
		color: "primary",
		icon: "fa-sharp-regular fa-video",
		href: request.meetingUrl,
		target: "_blank",
		children: t("consultancies.requests_start_video")
	}) : null;
	const paymentUrl = request.paymentUrl;
	const copied = copiedPaymentUrl === paymentUrl;
	return paymentUrl ? /* @__PURE__ */ jsx(Button, {
		width: "full",
		size: "sm",
		variant: "outline",
		color: "primary",
		icon: copied ? "fa-sharp-regular fa-check" : "fa-sharp-regular fa-copy",
		onClick: () => {
			onCopyPayment(paymentUrl);
		},
		children: copied ? t("consultancies.requests_copied") : t("consultancies.requests_copy_payment")
	}) : null;
}
function CardRepeatButton({ request }) {
	const { t } = useTranslation("core");
	const { submittingId, onConfirm } = useBoard();
	return /* @__PURE__ */ jsx(Button, {
		width: "full",
		size: "sm",
		variant: "solid",
		color: "primary",
		icon: "fa-sharp-regular fa-rotate-right",
		disabled: submittingId === request.id,
		onClick: () => {
			onConfirm(request);
		},
		children: t("consultancies.requests_reconfirm")
	});
}
/** The narrow rendering: the same cells stacked as a card. */
function RequestCard({ request, group }) {
	const { t } = useTranslation("core");
	return /* @__PURE__ */ jsxs("div", {
		className: css({
			borderColor: "grey.200",
			borderRadius: "xl",
			borderWidth: "2px",
			display: "flex",
			flexDir: "column",
			gap: "3",
			paddingX: "3",
			paddingY: "4"
		}),
		children: [
			/* @__PURE__ */ jsxs("div", {
				className: css({
					alignItems: "flex-start",
					display: "flex",
					gap: "2",
					justifyContent: "space-between"
				}),
				children: [/* @__PURE__ */ jsx(CardStatusBadge, {
					request,
					group
				}), /* @__PURE__ */ jsx(CardDate, {
					request,
					group
				})]
			}),
			/* @__PURE__ */ jsx(CardDetails, {
				request,
				group
			}),
			/* @__PURE__ */ jsx(RequestCell, {
				request,
				card: true
			}),
			/* @__PURE__ */ jsxs("div", {
				className: css({
					display: "grid",
					gap: "3",
					gridTemplateColumns: "repeat(2, minmax(0, 1fr))"
				}),
				children: [/* @__PURE__ */ jsx("div", {
					className: css({
						minWidth: "0",
						textAlign: "left"
					}),
					children: /* @__PURE__ */ jsx(CustomerCell, {
						request,
						card: true
					})
				}), /* @__PURE__ */ jsxs("div", {
					className: css({
						display: "flex",
						flexDir: "column",
						gap: "1",
						minWidth: "0",
						textAlign: "left"
					}),
					children: [/* @__PURE__ */ jsx("span", {
						className: css({
							fontSize: "sm",
							fontWeight: "bold"
						}),
						children: t("consultancies.requests_slots")
					}), /* @__PURE__ */ jsx(SlotsCell, {
						request,
						card: true
					})]
				})]
			}),
			group === "to-confirm" ? /* @__PURE__ */ jsx(CardToConfirmActions, { request }) : null,
			group === "scheduled" ? /* @__PURE__ */ jsx(CardScheduledActions, { request }) : null,
			group === "archived" ? /* @__PURE__ */ jsx(CardRepeatButton, { request }) : null
		]
	});
}
/**
* The "Tutte" overview reads every group through the listing fetch endpoint —
* the same query the tabs page through — capped to the preview sizes. Totals
* feed the tab badges and the "Vedi tutte" footers; bumping `refreshKey`
* re-reads them after a confirm or a reject lands.
*/
var useGroupPreviews = (fetchUrl, refreshKey) => {
	const mergeRouteContextHeaders = useRouteContextHeaders();
	const [state, setState] = useState({
		"to-confirm": {
			items: [],
			total: 0,
			loading: true
		},
		scheduled: {
			items: [],
			total: 0,
			loading: true
		},
		completed: {
			items: [],
			total: 0,
			loading: true
		},
		archived: {
			items: [],
			total: 0,
			loading: true
		}
	});
	useEffect(() => {
		if (!fetchUrl) {
			setState({
				"to-confirm": {
					items: [],
					total: 0,
					loading: false
				},
				scheduled: {
					items: [],
					total: 0,
					loading: false
				},
				completed: {
					items: [],
					total: 0,
					loading: false
				},
				archived: {
					items: [],
					total: 0,
					loading: false
				}
			});
			return;
		}
		let alive = true;
		setState((previous) => ({
			"to-confirm": {
				...previous["to-confirm"],
				loading: true
			},
			scheduled: {
				...previous.scheduled,
				loading: true
			},
			completed: {
				...previous.completed,
				loading: true
			},
			archived: {
				...previous.archived,
				loading: true
			}
		}));
		Promise.all(TAB_GROUPS.map(async (group) => {
			const url = `${fetchUrl}${fetchUrl.includes("?") ? "&" : "?"}status=${group}`;
			try {
				const response = await axios.get(url, { headers: Object.fromEntries(mergeRouteContextHeaders(url, { "X-Requested-With": "XMLHttpRequest" }).entries()) });
				if (!alive) return;
				setState((previous) => ({
					...previous,
					[group]: {
						items: (response.data.data.items ?? []).slice(0, PREVIEW_LIMITS[group]),
						total: response.data.data.totalItems ?? 0,
						loading: false
					}
				}));
			} catch {
				if (alive) setState((previous) => ({
					...previous,
					[group]: {
						items: [],
						total: 0,
						loading: false
					}
				}));
			}
		}));
		return () => {
			alive = false;
		};
	}, [
		fetchUrl,
		refreshKey,
		mergeRouteContextHeaders
	]);
	return state;
};
/** One capped table on the "Tutte" tab. The "Vedi tutte (N)" footer renders only when the group holds more rows than the preview shows. */
function GroupPreview({ group, preview, onViewAll }) {
	const { t } = useTranslation("core");
	const columns = useGroupColumns(group, false);
	const table = useDataTable({
		columns,
		data: preview.items,
		state: { sorting: [] },
		getRowId: (request) => request.id.toString()
	});
	if (!preview.loading && preview.total === 0) return null;
	return /* @__PURE__ */ jsxs("section", {
		"aria-labelledby": `consultancy-requests-group-${group}`,
		className: css({
			display: "flex",
			flexDir: "column",
			gap: "3"
		}),
		children: [group === "completed" ? /* @__PURE__ */ jsxs("div", {
			className: css({
				alignItems: "center",
				display: "flex",
				justifyContent: "space-between"
			}),
			children: [/* @__PURE__ */ jsx("h2", {
				id: `consultancy-requests-group-${group}`,
				className: css({
					fontSize: "lg",
					fontWeight: "medium"
				}),
				children: t(TAB_TITLES[group])
			}), /* @__PURE__ */ jsxs("a", {
				href: "https://www.tecnobit.it/admin/resources/order-product-commissions",
				target: "_blank",
				rel: "noreferrer",
				className: css({
					alignItems: "center",
					color: "accent.500",
					display: "inline-flex",
					fontSize: "sm",
					fontWeight: "medium",
					gap: "1"
				}),
				children: [t("consultancies.requests_compensi"), /* @__PURE__ */ jsx(FontAwesome, {
					name: "fa-sharp-regular fa-arrow-right",
					size: .8
				})]
			})]
		}) : /* @__PURE__ */ jsx("h2", {
			id: `consultancy-requests-group-${group}`,
			className: css({
				fontSize: "lg",
				fontWeight: "medium"
			}),
			children: t(TAB_TITLES[group])
		}), /* @__PURE__ */ jsx(DataTable, {
			table,
			caption: t(TAB_TITLES[group]),
			loading: preview.loading,
			dividers: false,
			verticalAlign: "middle",
			footer: preview.total > preview.items.length ? /* @__PURE__ */ jsx("div", {
				className: css({
					alignItems: "center",
					backgroundColor: {
						base: "transparent",
						tablet: "grey.50"
					},
					borderColor: "grey.200",
					borderRadius: {
						base: "0",
						tablet: "0"
					},
					borderWidth: "0",
					display: "flex",
					justifyContent: {
						base: "flex-start",
						tablet: "flex-end"
					},
					marginTop: {
						base: "2",
						tablet: "0"
					},
					paddingX: "4",
					paddingY: "2",
					w: "full"
				}),
				children: /* @__PURE__ */ jsx("button", {
					type: "button",
					onClick: () => {
						onViewAll(group);
					},
					className: css({
						color: "accent.500",
						cursor: "pointer",
						fontSize: "sm",
						fontWeight: "medium",
						textDecoration: "underline"
					}),
					children: t("consultancies.requests_view_all", { count: preview.total })
				})
			}) : void 0,
			renderCard: (row) => /* @__PURE__ */ jsx(RequestCard, {
				request: row.original,
				group
			}),
			emptyState: /* @__PURE__ */ jsx("div", {
				className: css({
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					paddingY: "8"
				}),
				children: /* @__PURE__ */ jsx("span", {
					className: css({ color: "text.300" }),
					children: t("consultancies.requests_empty")
				})
			})
		})]
	});
}
var getResponseError = async (response) => {
	const payload = await response.json().catch(() => null);
	if (!payload || typeof payload !== "object") return response.statusText;
	const data = payload;
	if (typeof data.message === "string") return data.message;
	if (!data.errors || typeof data.errors !== "object") return response.statusText;
	const firstError = Object.values(data.errors)[0];
	if (typeof firstError === "string") return firstError;
	return Array.isArray(firstError) && typeof firstError[0] === "string" ? firstError[0] : response.statusText;
};
/**
* The bookings a consultant has received.
*
* A tabbed worklist: "Tutte" previews every group capped with a "Vedi tutte"
* footer into the full tab, and each tab pages its whole group with lazy load
* on scroll. Counts arrive with the route payload for the first paint and go
* live once the previews answer.
*/
function ConsultancyRequests({ body }) {
	const { t } = useTranslation("core");
	const showToast = useShowToast();
	const notify = (type, message, duration = 1e4) => {
		showToast({
			type,
			message,
			duration
		});
	};
	const { route: { fetchUrl, counts }, locale } = usePage();
	const mergeRouteContextHeaders = useRouteContextHeaders();
	const { data, loading, currentPage, goToPage, applyFilters, currentFilters, setContainerRef } = usePagination();
	const activeTab = statusToTab(currentFilters.status);
	const [refreshKey, setRefreshKey] = useState(0);
	const [mobileTabMenuOpen, setMobileTabMenuOpen] = useState(false);
	const previews = useGroupPreviews(fetchUrl, refreshKey);
	const [visitorTimeZone, setVisitorTimeZone] = useState(null);
	const [confirming, setConfirming] = useState(null);
	const [confirmationError, setConfirmationError] = useState(null);
	const [scheduledAt, setScheduledAt] = useState("");
	const [hours, setHours] = useState("");
	const [submittingId, setSubmittingId] = useState(null);
	const [copiedPaymentUrl, setCopiedPaymentUrl] = useState(null);
	const copiedPaymentResetRef = useRef(null);
	const [rejecting, setRejecting] = useState(null);
	const dialogRef = useRef(null);
	const restoreFocusRef = useRef(null);
	const tabRefs = useRef({
		all: null,
		"to-confirm": null,
		scheduled: null,
		completed: null,
		archived: null
	});
	useEffect(() => {
		if (!confirming && !rejecting) return;
		restoreFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
		dialogRef.current?.querySelector(FOCUSABLE_IN_DIALOG)?.focus();
		const onKeyDown = (event) => {
			if (event.key === "Escape") {
				if (confirming) setConfirming(null);
				else setRejecting(null);
				return;
			}
			if (event.key !== "Tab") return;
			const focusable = Array.from(dialogRef.current?.querySelectorAll(FOCUSABLE_IN_DIALOG) ?? []);
			const first = focusable.at(0);
			const last = focusable.at(-1);
			if (!first || !last) return;
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		};
		document.addEventListener("keydown", onKeyDown);
		return () => {
			document.removeEventListener("keydown", onKeyDown);
			restoreFocusRef.current?.focus();
		};
	}, [confirming, rejecting]);
	useEffect(() => {
		return () => {
			if (copiedPaymentResetRef.current !== null) clearTimeout(copiedPaymentResetRef.current);
		};
	}, []);
	useEffect(() => {
		setVisitorTimeZone(getVisitorTimeZone());
	}, []);
	const sendAction = async (request, action, payload) => {
		setSubmittingId(request.id);
		const url = `/api/user/consultancies/${request.id.toString()}/${action}`;
		const headers = new Headers({
			Accept: "application/json",
			"Accept-Language": locale,
			"Content-Type": "application/json",
			"X-Requested-With": "XMLHttpRequest"
		});
		if (action === "confirm") headers.set("X-Consultancy-Timezone", getVisitorTimeZone());
		const csrfToken = getCsrfTokenFromCookie();
		if (csrfToken) headers.set("X-CSRF-TOKEN", csrfToken);
		try {
			const response = await fetch(url, {
				body: payload ? JSON.stringify(payload) : void 0,
				credentials: "same-origin",
				headers: Object.fromEntries(mergeRouteContextHeaders(url, headers).entries()),
				method: "POST"
			});
			if (!response.ok) throw new Error(await getResponseError(response));
			if (action === "confirm") setConfirming(null);
			else setRejecting(null);
			await goToPage(currentPage, true);
			setRefreshKey((key) => key + 1);
			notify("success", t(action === "confirm" ? "consultancies.requests_confirm_success" : "consultancies.requests_reject_success"), action === "reject" ? 3e3 : 1e4);
		} catch (error) {
			notify("error", error instanceof Error && error.message ? error.message : t("consultancies.requests_action_error"));
		} finally {
			setSubmittingId(null);
		}
	};
	const openConfirmation = (request) => {
		setConfirming(request);
		setConfirmationError(null);
		setScheduledAt(utcIsoToLocalDateTimeInput(request.scheduledAt));
		setHours(request.hours.toString());
	};
	const submitConfirmation = async () => {
		if (!confirming) return;
		setConfirmationError(null);
		const scheduledAtUtc = localDateTimeToUtcIso(scheduledAt);
		if (!scheduledAtUtc || new Date(scheduledAtUtc).getTime() <= Date.now()) {
			setConfirmationError(t("consultancies.requests_datetime_invalid"));
			return;
		}
		const payload = { scheduled_at: scheduledAtUtc };
		if (hours) {
			if (!/^\d+$/.test(hours) || Number(hours) < 1) {
				setConfirmationError(t("consultancies.requests_hours_invalid"));
				return;
			}
			payload.hours = Number(hours);
		}
		await sendAction(confirming, "confirm", payload);
	};
	const copyPaymentUrl = (paymentUrl) => {
		const clipboard = navigator.clipboard;
		if (!clipboard) {
			notify("error", t("consultancies.requests_action_error"));
			return;
		}
		clipboard.writeText(paymentUrl).then(() => {
			setCopiedPaymentUrl(paymentUrl);
			if (copiedPaymentResetRef.current !== null) clearTimeout(copiedPaymentResetRef.current);
			copiedPaymentResetRef.current = setTimeout(() => {
				setCopiedPaymentUrl((current) => current === paymentUrl ? null : current);
				copiedPaymentResetRef.current = null;
			}, 3e3);
		}).catch(() => {
			notify("error", t("consultancies.requests_action_error"));
		});
	};
	const selectTab = (tab) => {
		if (loading) return;
		applyFilters({
			status: tab === "all" ? void 0 : tab,
			sort: void 0
		});
	};
	const focusTab = (tab) => {
		selectTab(tab);
		tabRefs.current[tab]?.focus();
	};
	const onTabKeyDown = (event, tab) => {
		const order = [
			"all",
			"to-confirm",
			"scheduled",
			"completed",
			"archived"
		];
		const index = order.indexOf(tab);
		if (event.key === "ArrowRight") {
			event.preventDefault();
			focusTab(order[(index + 1) % order.length]);
		} else if (event.key === "ArrowLeft") {
			event.preventDefault();
			focusTab(order[(index + order.length - 1) % order.length]);
		} else if (event.key === "Home") {
			event.preventDefault();
			focusTab(order[0]);
		} else if (event.key === "End") {
			event.preventDefault();
			focusTab(order[order.length - 1]);
		}
	};
	const countFor = (group) => {
		const preview = previews[group];
		return !preview.loading || preview.total > 0 || preview.items.length > 0 ? preview.total : counts?.[COUNT_KEYS[group]] ?? 0;
	};
	const board = useMemo(() => ({
		timeZone: visitorTimeZone,
		submittingId,
		copiedPaymentUrl,
		onConfirm: openConfirmation,
		onReject: (request) => {
			setRejecting(request);
		},
		onCopyPayment: copyPaymentUrl
	}), [
		visitorTimeZone,
		submittingId,
		rejecting,
		copiedPaymentUrl,
		scheduledAt,
		hours,
		locale,
		currentPage
	]);
	const tabs = [{
		id: "all",
		label: t("consultancies.requests_tab_all"),
		count: null
	}, ...TAB_GROUPS.map((group) => ({
		id: group,
		label: t(TAB_TITLES[group]),
		count: countFor(group)
	}))];
	const selectedTab = tabs.find((tab) => tab.id === activeTab) ?? tabs[0];
	return /* @__PURE__ */ jsxs("div", {
		ref: (ref) => {
			setContainerRef(activeTab === "all" ? null : ref);
		},
		className: css({
			w: "full",
			display: "flex",
			flexDir: "column",
			gap: "4"
		}),
		children: [
			body ? /* @__PURE__ */ jsx(Markdown, { children: body }) : null,
			/* @__PURE__ */ jsxs("div", {
				className: css({
					display: {
						base: "block",
						tablet: "none"
					},
					position: "relative"
				}),
				children: [/* @__PURE__ */ jsx("button", {
					type: "button",
					"aria-controls": "consultancy-requests-mobile-menu",
					"aria-expanded": mobileTabMenuOpen,
					"aria-haspopup": "menu",
					disabled: loading,
					onClick: () => {
						setMobileTabMenuOpen((open) => !open);
					},
					className: css({
						alignItems: "center",
						borderBottomColor: "grey.200",
						borderBottomWidth: "1px",
						cursor: "pointer",
						display: "flex",
						justifyContent: "flex-start",
						width: "full"
					}),
					children: /* @__PURE__ */ jsxs("span", {
						className: css({
							alignItems: "center",
							borderBottomColor: "accent.500",
							borderBottomWidth: "2px",
							color: "accent.500",
							display: "inline-flex",
							fontSize: "sm",
							fontWeight: "medium",
							gap: "1.5",
							marginBottom: "-1px",
							paddingX: "1",
							paddingY: "2"
						}),
						children: [
							selectedTab.label,
							selectedTab.count !== null ? /* @__PURE__ */ jsx("span", {
								className: css({
									backgroundColor: "grey.50",
									borderRadius: "full",
									color: "text.300",
									fontSize: "sm",
									paddingX: "1.5",
									paddingY: "0.5"
								}),
								children: selectedTab.count
							}) : null,
							/* @__PURE__ */ jsx("span", {
								"aria-hidden": "true",
								className: mobileTabMenuOpen ? css({
									display: "inline-flex",
									transform: "rotate(180deg)",
									transformOrigin: "center center",
									transition: "transform 150ms ease-in-out"
								}) : css({
									display: "inline-flex",
									transform: "rotate(0deg)",
									transformOrigin: "center center",
									transition: "transform 150ms ease-in-out"
								}),
								children: /* @__PURE__ */ jsx(FontAwesome, {
									name: "fa-sharp-regular fa-chevron-down",
									size: .875
								})
							})
						]
					})
				}), mobileTabMenuOpen ? /* @__PURE__ */ jsx("div", {
					id: "consultancy-requests-mobile-menu",
					role: "menu",
					className: css({
						backgroundColor: "white",
						borderColor: "grey.200",
						borderRadius: "md",
						borderWidth: "1px",
						boxShadow: "lg",
						display: "flex",
						flexDir: "column",
						insetX: "0",
						overflow: "hidden",
						position: "absolute",
						top: "calc(100% + 4px)",
						zIndex: 10
					}),
					children: tabs.filter((tab) => tab.id !== activeTab).map((tab) => /* @__PURE__ */ jsxs("button", {
						type: "button",
						role: "menuitem",
						disabled: loading,
						onClick: () => {
							setMobileTabMenuOpen(false);
							selectTab(tab.id);
						},
						className: css({
							alignItems: "center",
							color: "text.300",
							cursor: "pointer",
							display: "flex",
							fontSize: "sm",
							gap: "1.5",
							justifyContent: "space-between",
							paddingX: "3",
							paddingY: "2",
							textAlign: "left",
							_hover: { backgroundColor: "grey.50" }
						}),
						children: [tab.label, tab.count !== null ? /* @__PURE__ */ jsx("span", {
							className: css({
								backgroundColor: "grey.50",
								borderRadius: "full",
								color: "text.300",
								fontSize: "sm",
								paddingX: "1.5",
								paddingY: "0.5"
							}),
							children: tab.count
						}) : null]
					}, tab.id))
				}) : null]
			}),
			/* @__PURE__ */ jsx("div", {
				role: "tablist",
				"aria-label": t("consultancies.requests_table_caption"),
				className: css({
					borderBottomWidth: "1px",
					borderBottomColor: "grey.200",
					display: {
						base: "none",
						tablet: "flex"
					},
					flexWrap: "wrap",
					gap: "4"
				}),
				children: tabs.map((tab) => {
					const selected = activeTab === tab.id;
					return /* @__PURE__ */ jsxs("button", {
						ref: (ref) => {
							tabRefs.current[tab.id] = ref;
						},
						type: "button",
						role: "tab",
						id: `consultancy-requests-tab-${tab.id}`,
						"aria-selected": selected,
						"aria-controls": "consultancy-requests-panel",
						tabIndex: selected ? 0 : -1,
						disabled: loading,
						onClick: () => {
							selectTab(tab.id);
						},
						onKeyDown: (event) => {
							onTabKeyDown(event, tab.id);
						},
						className: css({
							alignItems: "center",
							borderBottomWidth: "2px",
							borderBottomColor: selected ? "accent.500" : "transparent",
							color: selected ? "accent.500" : "text.300",
							cursor: "pointer",
							display: "inline-flex",
							fontSize: "sm",
							fontWeight: selected ? "medium" : "normal",
							gap: "1.5",
							marginBottom: "-1px",
							paddingX: "1",
							paddingY: "2"
						}),
						children: [tab.label, tab.count !== null ? /* @__PURE__ */ jsx("span", {
							className: css({
								backgroundColor: "grey.50",
								borderRadius: "full",
								color: "text.300",
								fontSize: "sm",
								paddingX: "1.5",
								paddingY: "0.5"
							}),
							children: tab.count
						}) : null]
					}, tab.id);
				})
			}),
			/* @__PURE__ */ jsx(BoardContext.Provider, {
				value: board,
				children: /* @__PURE__ */ jsx("div", {
					role: "tabpanel",
					id: "consultancy-requests-panel",
					"aria-label": t("consultancies.requests_table_caption"),
					children: activeTab === "all" ? /* @__PURE__ */ jsx("div", {
						className: css({
							display: "flex",
							flexDir: "column",
							gap: "8"
						}),
						children: TAB_GROUPS.map((group) => /* @__PURE__ */ jsx(GroupPreview, {
							group,
							preview: previews[group],
							onViewAll: (next) => {
								selectTab(next);
							}
						}, group))
					}) : /* @__PURE__ */ jsx(GroupFullTable, {
						group: activeTab,
						data,
						loading
					})
				})
			}),
			rejecting ? /* @__PURE__ */ jsx("div", {
				role: "dialog",
				"aria-modal": "true",
				"aria-labelledby": "consultancy-rejection-title",
				className: css({
					alignItems: "center",
					backgroundColor: "black/50",
					display: "flex",
					inset: "0",
					justifyContent: "center",
					padding: "4",
					position: "fixed",
					zIndex: 50
				}),
				children: /* @__PURE__ */ jsxs("form", {
					ref: dialogRef,
					onSubmit: (event) => {
						event.preventDefault();
						sendAction(rejecting, "reject");
					},
					className: css({
						backgroundColor: "white",
						borderRadius: "lg",
						display: "flex",
						flexDir: "column",
						gap: "4",
						maxWidth: "lg",
						padding: {
							base: "4",
							tablet: "6"
						},
						width: "full"
					}),
					children: [
						/* @__PURE__ */ jsxs("div", {
							className: css({
								alignItems: "center",
								display: "flex",
								justifyContent: "space-between"
							}),
							children: [/* @__PURE__ */ jsx("h2", {
								id: "consultancy-rejection-title",
								className: css({
									fontSize: "lg",
									fontWeight: "medium"
								}),
								children: t("consultancies.requests_reject")
							}), /* @__PURE__ */ jsx("button", {
								type: "button",
								"aria-label": t("consultancies.requests_cancel"),
								onClick: () => {
									setRejecting(null);
								},
								className: css({
									color: "text.300",
									cursor: "pointer",
									padding: "1"
								}),
								children: /* @__PURE__ */ jsx(FontAwesome, {
									name: "fa-sharp-regular fa-xmark",
									size: 1
								})
							})]
						}),
						/* @__PURE__ */ jsx("p", {
							className: css({
								color: "text.700",
								fontSize: "md",
								lineHeight: "relaxed"
							}),
							children: t("consultancies.requests_reject_confirm")
						}),
						/* @__PURE__ */ jsxs("div", {
							className: css({
								display: "flex",
								justifyContent: "flex-end",
								gap: "2"
							}),
							children: [/* @__PURE__ */ jsx("button", {
								type: "button",
								onClick: () => {
									setRejecting(null);
								},
								className: css({
									color: "text.300",
									cursor: "pointer",
									paddingX: "3",
									paddingY: "2"
								}),
								children: t("consultancies.requests_cancel")
							}), /* @__PURE__ */ jsx("button", {
								type: "submit",
								disabled: submittingId === rejecting.id,
								className: css({
									backgroundColor: "error.700/20",
									borderRadius: "md",
									color: "error.700",
									cursor: "pointer",
									fontWeight: "medium",
									paddingX: "3",
									paddingY: "2"
								}),
								children: t("consultancies.requests_reject_action")
							})]
						})
					]
				})
			}) : null,
			confirming ? /* @__PURE__ */ jsx("div", {
				role: "dialog",
				"aria-modal": "true",
				"aria-labelledby": "consultancy-confirmation-title",
				className: css({
					alignItems: "center",
					backgroundColor: "black/50",
					display: "flex",
					inset: "0",
					justifyContent: "center",
					padding: "4",
					position: "fixed",
					zIndex: 50
				}),
				children: /* @__PURE__ */ jsxs("form", {
					ref: dialogRef,
					onSubmit: (event) => {
						event.preventDefault();
						submitConfirmation();
					},
					className: css({
						backgroundColor: "white",
						borderRadius: "lg",
						display: "flex",
						flexDir: "column",
						gap: "4",
						maxWidth: "lg",
						padding: {
							base: "4",
							tablet: "6"
						},
						width: "full"
					}),
					children: [
						/* @__PURE__ */ jsxs("div", {
							className: css({
								alignItems: "center",
								display: "flex",
								justifyContent: "space-between"
							}),
							children: [/* @__PURE__ */ jsx("h2", {
								id: "consultancy-confirmation-title",
								className: css({
									fontSize: "lg",
									fontWeight: "medium"
								}),
								children: t("consultancies.requests_confirm_title")
							}), /* @__PURE__ */ jsx("button", {
								type: "button",
								"aria-label": t("consultancies.requests_cancel"),
								onClick: () => {
									setConfirming(null);
								},
								className: css({
									color: "text.300",
									cursor: "pointer",
									padding: "1"
								}),
								children: /* @__PURE__ */ jsx(FontAwesome, {
									name: "fa-sharp-regular fa-xmark",
									size: 1
								})
							})]
						}),
						confirmationError ? /* @__PURE__ */ jsx("p", {
							role: "alert",
							className: css({
								borderRadius: "md",
								borderWidth: "1px",
								borderColor: "error.500",
								color: "error.500",
								paddingX: "3",
								paddingY: "2"
							}),
							children: confirmationError
						}) : null,
						confirming.slotProposals.length ? /* @__PURE__ */ jsxs("div", {
							className: css({
								display: "flex",
								flexDir: "column",
								gap: "2"
							}),
							children: [/* @__PURE__ */ jsxs("span", {
								className: css({
									color: "text.300",
									fontSize: "sm"
								}),
								children: [t("consultancies.requests_proposed_slots"), ":"]
							}), /* @__PURE__ */ jsx("div", {
								className: css({
									display: "flex",
									flexWrap: "wrap",
									gap: "2"
								}),
								children: confirming.slotProposals.map((slot) => {
									const localSlot = utcIsoToLocalDateTimeInput(slot);
									return /* @__PURE__ */ jsx("button", {
										type: "button",
										onClick: () => {
											setScheduledAt(localSlot);
										},
										className: css({
											borderWidth: "1px",
											borderColor: scheduledAt === localSlot ? "accent.500" : "grey.200",
											borderRadius: "md",
											color: scheduledAt === localSlot ? "accent.500" : "text.300",
											cursor: "pointer",
											fontSize: "sm",
											paddingX: "3",
											paddingY: "2"
										}),
										children: formatShortDate(slot, locale, visitorTimeZone, t("consultancies.requests_today"))
									}, slot);
								})
							})]
						}) : null,
						/* @__PURE__ */ jsx(DatePicker, {
							id: "consultancy-confirmation-scheduled-at",
							label: t("consultancies.requests_confirm_datetime"),
							locale,
							required: true,
							range: "future",
							showTime: true,
							showValidIcon: false,
							value: scheduledAt ? /* @__PURE__ */ new Date(`${scheduledAt}:00`) : null,
							format: "Pp",
							onChange: (date) => {
								setScheduledAt(date ? utcIsoToLocalDateTimeInput(date.toISOString()) : "");
							}
						}),
						/* @__PURE__ */ jsxs("label", {
							className: css({
								display: "flex",
								flexDir: "column",
								gap: "1"
							}),
							children: [/* @__PURE__ */ jsx("span", {
								className: css({ fontWeight: "medium" }),
								children: t("consultancies.requests_confirm_hours")
							}), /* @__PURE__ */ jsx("input", {
								type: "number",
								min: "1",
								step: "1",
								value: hours,
								onChange: (event) => {
									setHours(event.target.value);
								},
								className: css({
									borderWidth: "1px",
									borderColor: "grey.200",
									borderRadius: "md",
									paddingX: "3",
									paddingY: "2"
								})
							})]
						}),
						/* @__PURE__ */ jsxs("div", {
							className: css({
								display: "flex",
								justifyContent: "flex-end",
								gap: "2"
							}),
							children: [/* @__PURE__ */ jsx("button", {
								type: "button",
								onClick: () => {
									setConfirming(null);
								},
								className: css({
									color: "text.300",
									cursor: "pointer",
									paddingX: "3",
									paddingY: "2"
								}),
								children: t("consultancies.requests_cancel")
							}), /* @__PURE__ */ jsx("button", {
								type: "submit",
								disabled: submittingId === confirming.id,
								className: css({
									backgroundColor: "accent.500",
									borderRadius: "md",
									color: "overAccent.500",
									cursor: "pointer",
									fontWeight: "medium",
									paddingX: "3",
									paddingY: "2"
								}),
								children: t("consultancies.requests_confirm_submit")
							})]
						})
					]
				})
			}) : null
		]
	});
}
/**
* The whole group, paged with lazy load on scroll. Ordering travels in the
* same filter channel as the tab, so it lands in the address, survives
* back/forward and composes with the tab instead of sitting beside it. Wire
* format: the column id ascending, the same prefixed with `-` descending.
*/
function GroupFullTable({ group, data, loading }) {
	const { t } = useTranslation("core");
	const { currentFilters, applyFilters } = usePagination();
	const columns = useGroupColumns(group, true);
	/**
	* Column ids are the wire identifiers the listing query resolves against its allowlist,
	* so only the columns it can actually order declare `enableSorting`.
	*/
	const sorting = useMemo(() => {
		const raw = currentFilters.sort;
		if (typeof raw !== "string" || raw === "" || raw === "-") return [];
		const desc = raw.startsWith("-");
		return [{
			id: desc ? raw.slice(1) : raw,
			desc
		}];
	}, [currentFilters.sort]);
	const table = useDataTable({
		columns,
		data,
		state: { sorting },
		getRowId: (request) => request.id.toString(),
		onSortingChange: (updater) => {
			const first = (typeof updater === "function" ? updater(sorting) : updater).at(0);
			applyFilters({ sort: first ? `${first.desc ? "-" : ""}${first.id}` : void 0 });
		}
	});
	return /* @__PURE__ */ jsx(DataTable, {
		table,
		caption: t(TAB_TITLES[group]),
		loading,
		dividers: false,
		verticalAlign: "middle",
		sortIcons: {
			ascendingIconName: "fa-sharp-regular fa-sort-up",
			descendingIconName: "fa-sharp-regular fa-sort-down",
			unsortedIconName: "fa-sharp-regular fa-sort"
		},
		renderCard: (row) => /* @__PURE__ */ jsx(RequestCard, {
			request: row.original,
			group
		}),
		emptyState: /* @__PURE__ */ jsxs("div", {
			className: css({
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				paddingY: "16"
			}),
			children: [/* @__PURE__ */ jsx(FontAwesome, {
				name: "fa-sharp-regular fa-inbox",
				size: 10,
				css: css.raw({ color: "grey.200" })
			}), /* @__PURE__ */ jsx("span", {
				className: css({
					color: "text.300",
					paddingTop: "8"
				}),
				children: t("consultancies.requests_empty")
			})]
		})
	});
}
//#endregion
export { consultancy_requests_exports as n, ConsultancyRequests as t };
